
import { defineComponent } from "vue";
import { required, maxLength, minLength } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import numOnly from "@/utils/validations/numOnly";
import moment from "moment";
import messages from "@/config/messages";
import helper from "@/utils/helper";
import common from "@/config/common";
import routesPath from "@/config/routesPath";
import TokenService from "@/services/tokenService";
import * as apiClient from "@/apiclient/";

export default defineComponent({
  name: "OtpValidationView",
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      isFormInvalid: false,
      verificationCode: "",
      countdown: this.getCountDownTimer(),
      validationMsg:
        "You entered an incorrect or expired code. Please try again or click RESEND CODE to receive a new code.",
      isSubmitDisabled: false,
      otpResponseMessage: "We have sent a verification code to",
      lastDateMsg: "",
      isResponseValid: true,
      messages,
      routesPath,
    };
  },
  validations() {
    return {
      verificationCode: {
        required,
        numOnly,
        maxLength: maxLength(6),
        minLength: minLength(6),
      },
    };
  },
  mounted() {
    this.timerCountdown();
    let { lastDateToEnroll, remainingDaysToEnroll } = this.loginData;
    if (lastDateToEnroll && remainingDaysToEnroll) {
      const formattedLastDate = helper.formatDate(
        lastDateToEnroll,
        "MM/DD/YYYY HH:mm"
      );
      const now = moment();
      const end = moment(lastDateToEnroll);
      const diffMs = end.diff(now);
      if (diffMs <= 0) {
        this.lastDateMsg = `Enrollment Ended on ${formattedLastDate}`;
      } else {
        const duration = moment.duration(diffMs);
        const days = Math.floor(duration.asDays());
        const hours = duration.hours();
        const minutes = duration.minutes();

        if (days > 0) {
          this.lastDateMsg = `Enrollment ends in ${days} day${
            days > 1 ? "s" : ""
          }`;
        } else if (hours > 0) {
          this.lastDateMsg = `Enrollment ends in ${hours} hour${
            hours > 1 ? "s" : ""
          } and ${minutes} minute${minutes !== 1 ? "s" : ""}.`;
        } else {
          this.lastDateMsg = `Enrollment ends in ${minutes} minute${
            minutes !== 1 ? "s" : ""
          }.`;
        }
      }
    }
  },
  computed: {
    loginData() {
      const storedData = sessionStorage.getItem(
        common.storageVariables.loginData
      );
      return storedData ? JSON.parse(storedData) : null;
    },
    formatedCountdown(): string {
      return moment(this.countdown, "seconds").format("m:ss");
    },
    resendTextEnable(): string {
      return this.countdown ? "disabled" : "";
    },
  },
  methods: {
    getCountDownTimer() {
      const otpTimer = sessionStorage.getItem(common.storageVariables.otpTimer);
      return otpTimer ? Number(otpTimer) : Number(common.otpTimer);
    },
    maskEmail() {
      const email = this.loginData.email;

      if (this.loginData.firstLogin) {
        return email;
      }

      const lengthToShow = 7;
      const maskedPart = "*".repeat(email.length - lengthToShow - 2);
      const visiblePart = email.slice(-lengthToShow);
      const emailStartPart = email.slice(0, 2);
      return emailStartPart + maskedPart + visiblePart;
    },
    hideErrors(field: string): void {
      this.v$[field].$touch();
      this.isFormInvalid = false;
    },
    timerCountdown(): void {
      const stopCountdown = setInterval(() => {
        if (this.countdown > 0) this.countdown -= 1;
        sessionStorage.setItem(
          common.storageVariables.otpTimer,
          this.countdown.toString()
        );
        if (!this.countdown) clearInterval(stopCountdown);
      }, 1000);
    },
    resendCode(): void {
      if (this.countdown == 0) {
        const responseData = this.sendOtp().catch.length;
        if (responseData > 0) {
          this.otpResponseMessage = "We have re-sent a verification code to";
          this.countdown = Number(common.otpTimer);
          this.timerCountdown();
        }
      }
    },
    buttonDesign(): string {
      const buttonStatus = this.v$.verificationCode.$invalid;
      return buttonStatus == true ? "btn-secondary" : "btn-primary";
    },
    async sendOtp(): Promise<void> {
      const params = {
        registrationCode: this.loginData.registrationCode,
        ssn: this.loginData.ssn,
        emailAddress: this.loginData.email,
      };
      const result = await apiClient
        .loginController()
        .postApiLoginEmailVerification(params);
      if (!result || result.error) {
        this.isFormInvalid = true;
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
        }
        return;
      } else if (result.data) {
        this.isFormInvalid = false;
        this.isResponseValid = true;
      }
    },
    async otpVerification(): Promise<void> {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }
      const params = {
        verificationCode: this.verificationCode,
        emailAddress: this.loginData.email,
      };
      this.isSubmitDisabled = true;
      const result = await apiClient
        .loginController()
        .postApiLoginCodeVerification(params);
      this.isSubmitDisabled = false;
      this.verificationCode = "";
      this.v$.verificationCode.$reset();

      if (!result || result.error) {
        this.isFormInvalid = true;
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
        }
        return;
      } else if (result.data) {
        TokenService.setAccessToken(result.data.token!);
        TokenService.setRefreshToken(result.data.refreshToken!);
        if (result.data.status === 10) {
          sessionStorage.setItem(common.storageVariables.isSubmitted, "yes");
        } else {
          this.$router.push({
            name: routesPath.enrollments,
          });
        }
        sessionStorage.removeItem(common.storageVariables.applicationSubmitted);
        sessionStorage.removeItem(common.storageVariables.loginData);
        sessionStorage.removeItem(common.storageVariables.otpTimer);
        sessionStorage.removeItem(common.storageVariables.allBenefitsWaived);
        sessionStorage.removeItem(common.storageVariables.paymentMethod);
        this.isFormInvalid = false;
        this.$router.push({
          name: routesPath.enrollments,
        });
      }
    },
  },
});
